// When innernet can't reach itself, it says so.
//
// The rule this exists to enforce: **a failure must never render as emptiness.**
// An error that has been coalesced into `?? []` becomes a page that calmly tells
// someone their memory is gone — which is what happened on 2026-07-29, and which
// is the single worst sentence this product can say by accident.
//
// So every surface that fails to reach memory reports it here, one place, and
// one quiet window near netti answers for the whole app. The window then asks
// /api/health directly: if the service is genuinely down it says so plainly and
// points at /status; if the service is fine, the trouble was local (this route,
// this moment) and it says that instead. Two different sentences, because they
// are two different situations and the person deserves to know which one they
// are in.

import { fetchWithDeadline, type DeadlineInit } from "./fetchDeadline";

export type OutageReason = "memory" | "network" | "unknown";

export interface OutageSignal {
  reason: OutageReason;
  /** what the person was trying to reach — "your innernet", "this room" */
  what?: string;
  at: number;
}

const EVENT = "innernet:unreachable";

/** Say that something could not be reached. Safe to call from anywhere on the
 *  client; on the server it is a no-op (the page renders its own honest state). */
export function reportUnreachable(reason: OutageReason = "unknown", what?: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<OutageSignal>(EVENT, { detail: { reason, what, at: Date.now() } }));
}

export function onUnreachable(fn: (s: OutageSignal) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const h = (e: Event) => fn((e as CustomEvent<OutageSignal>).detail);
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
}

/** Ask the service whether IT is down, or whether it was just this request.
 *  Anonymous and content-free — the same probe an uptime monitor hits.
 *
 *  ON A DEADLINE, deliberately. The probe reaches the database, so when the
 *  database is the thing that is unwell this call is exactly the call that
 *  hangs — and a window frozen on "checking whether it's us…" is worse than
 *  either answer, because it leaves someone waiting mid-sentence about their
 *  own memory. A probe that will not answer inside a few seconds IS the answer. */
export async function serviceIsUp(timeoutMs = 4_000): Promise<boolean> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch("/api/health", { cache: "no-store", signal: ctl.signal });
    if (!res.ok) return false;
    const body = (await res.json()) as { ok?: boolean };
    return body.ok !== false;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/* ------------------------------------------------------------------ signed out */

// A 401 is not an outage and not emptiness: the session ended, the memory is
// fine, and everything already on screen is still true. It gets its own signal,
// so the one surface that owns the page (the shell) can say so ONCE, quietly,
// with a way back in, and keep the tree it has loaded.

const SIGNED_OUT = "innernet:signed-out";

/** Say that the session ended. Safe anywhere on the client; a no-op on the server. */
export function reportSignedOut(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SIGNED_OUT));
}

export function onSignedOut(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(SIGNED_OUT, fn);
  return () => window.removeEventListener(SIGNED_OUT, fn);
}

// ...and the session is good again: any read of the memory that answers. A 401
// once, from a moment the auth server could not check, must never leave "please
// sign in again" on screen for a person who is plainly still signed in.
const SIGNED_IN = "innernet:signed-in";

function reportSignedIn(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SIGNED_IN));
}

export function onSignedIn(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(SIGNED_IN, fn);
  return () => window.removeEventListener(SIGNED_IN, fn);
}

/** A fetch that reports its own failure before it returns. Use this for any
 *  read of someone's memory: the caller still handles the null, but the window
 *  has already been told, so the screen can never go quietly empty.
 *
 *  It also has a DEADLINE (fetchWithDeadline): a read that never answers is
 *  reported as unreachable like one that failed, instead of holding its caller
 *  forever. A caller that cancels (a signal it passed) is not an outage. */
export async function reachOrReport(url: string, what: string, init?: DeadlineInit): Promise<Response | null> {
  try {
    const res = await fetchWithDeadline(url, init);
    if (res.status === 401) {
      reportSignedOut();
      return res;
    }
    // 5xx is ours; 4xx is usually a real answer (gone, not yours) and belongs to
    // the caller to render honestly.
    if (res.status >= 500) {
      reportUnreachable("memory", what);
      return null;
    }
    if (res.ok) reportSignedIn();
    return res;
  } catch {
    if (init?.signal?.aborted) return null; // the caller moved on; nothing is wrong
    reportUnreachable("network", what);
    return null;
  }
}

/** The same read, for a speculative one (a prefetch, a poll): it never speaks
 *  about an outage, because the open that follows will, but it does notice a
 *  session that ended, and it too has a deadline. null = no usable answer. */
export async function reachQuietly(url: string, init?: DeadlineInit): Promise<Response | null> {
  try {
    const res = await fetchWithDeadline(url, init);
    if (res.status === 401) reportSignedOut();
    else if (res.ok) reportSignedIn();
    return res;
  } catch {
    return null;
  }
}
